import { DateUtils } from 'src/utils/date'
import { COMMUNITY_DEFAULT_IMAGE } from 'src/utils/constants'
import i18n from 'src/i18n'
import InputMarkdown from 'src/components/InputMarkdown'
import { useState } from 'react'
import { useUser } from 'src/hook/useUser'
import clsx from 'clsx'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { paths } from 'src/router/paths'
import { Actions, ComponentNames, Entities, EventNames, ScreenNames } from 'src/utils/datahub/enums'
import useSendDatahubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { CommunityCardProps, PathParams } from './communityCard.types'
import { ImageWrapper, InputWrapper, Wrapper } from './communityCard.styles'

const CommunityCard = ({ community, border }: CommunityCardProps) => {
  const { lang, membership } = useParams() as PathParams
  const [openInputMarkdown, setOpenInputMarkdown] = useState(false)
  const { theme } = useUser()
  const navigate = useNavigate()
  const { sendDataHubConsume } = useSendDatahubConsume()

  const handleOpenInputMarkdown = () => setOpenInputMarkdown(true)
  const handleCloseInputMarkdown = () => setOpenInputMarkdown(false)

  const redirectToCommunity = () => {
    const path = generatePath(paths.COMMUNITY_PATH, {
      lang: lang,
      membership: membership,
      idCommunity: String(community.id)
    })

    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.CLICK_TO_CREATE_A_POST_ON_SEARCH,
        screenName: ScreenNames.COMMUNITY_NO_SEARCH_RESULTS,
        componentName: ComponentNames.NO_SEARCH_RESULTS
      }
    })

    navigate(path)
  }

  const handleClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement
    const isInput = target.closest('[data-test="input"]')

    if (!isInput) {
      handleCloseInputMarkdown()
      redirectToCommunity()
    }
  }

  return (
    <Wrapper
      className={clsx(
        '_d-flex _py-5 _px-2 _flex-column _flex-xl-row _align-items-xl-center _cursor-pointer',
        {
          '_border-bottom _border-gray-200': border
        }
      )}
      data-test="community-card"
      onClick={handleClick}
    >
      <div className="_d-flex _align-items-center">
        <ImageWrapper className="_mr-5 _rounded-lg _overflow-hidden">
          <img
            src={community.imageURL || COMMUNITY_DEFAULT_IMAGE}
            alt={community.name}
            loading="lazy"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = COMMUNITY_DEFAULT_IMAGE
            }}
          />
        </ImageWrapper>

        <div>
          <h3 className="_text-2 _mb-0">{community.name}</h3>
          <span className="_text-1 _text-gray-600">
            {DateUtils.compareDates(community.lastPostDate, true)}
          </span>
        </div>
      </div>

      <InputWrapper
        className="_pt-3 _pt-xl-0 _ml-xl-auto _rounded-lg _cursor-default"
        theme={theme}
        data-test="input"
        onClick={handleOpenInputMarkdown}
      >
        <InputMarkdown
          placeholder={i18n.t(
            'no_search_results.communities_section.create_post_input.placeholder'
          )}
          communityId={community.id}
          openMarkdown={openInputMarkdown}
          setOpenInputMarkdown={setOpenInputMarkdown}
          onSuccess={redirectToCommunity}
        />
      </InputWrapper>
    </Wrapper>
  )
}

export default CommunityCard
